import React, {useCallback, useEffect} from 'react';
import {FadeProps} from './Fade/Fade';
import {GrowProps} from './Grow/Grow';
import {SlideProps} from './Slide/Slide';
import {getTimeouts} from './utils';

export type TransitionDuration = number | {mount: number; unmount: number};
export type TransitionDelay = number | {mount: number; unmount: number};
export interface TransitionProps {
  mounted: boolean;
  duration: TransitionDuration;
  delay?: TransitionDelay;
  nodeRef: React.RefObject<HTMLElement | null>;
  onUnmount?: VoidFunction;
  preventAnimationOnMount?: boolean;
}

export type TransitionComponentType =
  | React.FC<SlideProps>
  | React.FC<FadeProps>
  | React.FC<GrowProps>;

export const Transition: React.FC<React.PropsWithChildren<TransitionProps>> = ({
  mounted: mountedProp,
  duration,
  delay,
  nodeRef,
  children,
  onUnmount,
}) => {
  const [isMounted, setIsMounted] = React.useState<boolean>(mountedProp);

  const performUnmount = useCallback(() => {
    const timeouts = getTimeouts({duration, delay});

    setIsMounted(true);
    // set mounted state to false to allow transition to end
    setTimeout(() => {
      if (nodeRef.current) {
        setIsMounted(false);
        onUnmount?.();
      }
    }, timeouts.unmountDelay + timeouts.unmountDuration);
  }, [delay, duration, nodeRef]);

  useEffect(() => {
    if (mountedProp) {
      if (!isMounted) {
        // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
        setIsMounted(true);
      }
    } else {
      if (isMounted) {
        performUnmount();
      }
    }
  }, [mountedProp, isMounted, performUnmount]);

  return isMounted ? <>{children}</> : null;
};
